import domClass from 'dojo/dom-class';
import forIn from 'lodash/forIn';

import Mediator from 'gisw/Classes/Mediator';
// @ts-ignore
import type from 'gisw/Helpers/type';
import messageObserver from 'gisw/Widgets/MessageManager/MessageObserver';
import NodeHash from 'gisw/Widgets/NodeHash';

import toolUrl from 'src/modules/tool-url';
import updateCheck from 'src/update-check';

/**
 * The following methods and objects configure how tools are shown.
 */
const DONT_SHOW_SET = new Set([
  'SALESSEARCH',
  'PAYMENTSEARCH',
  'TAXCART',
  'INSTRUMENTSEARCH',
  'QUICKSEARCH',
  'MEDIAMANAGEMENT',
  'ALWAYSONIDENTIFY',
  'STREETVIEW'
]);

/**
 * Insert all tools that will need to
 * shutdown the AlwaysOnIdentify tool
 * @type {Set<FactoryTypes.ModuleNames>}
 */
const PAUSE_ALWAYS_ON_ID = new Set([
  'Buffer',
  'Draw',
  'Measure',
  'Landuse',
  'Identify',
  'ActivityManagement'
]);

/**
 * @typedef {'blue' | 'green' | 'purple' | 'red'} ToolColor
 */

/** @type {Array<ToolColor>} */
const COLOR_KEYS = ['blue', 'green', 'purple', 'red'];

/** @type {Map<string, ToolColor>} */
const COLOR_MAP = new Map([
  ['ADDRESS', 'red'],
  ['BUFFER', 'red'],
  ['BOOKMARK', 'blue'],
  ['BOOKMARKPUBLIC', 'blue'],
  ['BULKIMAGEUPLOADER', 'green'],
  ['DRAW', 'purple'],
  ['IDENTIFY', 'green'],
  ['LANDUSE', 'red'],
  ['MAPNAVIGATION', 'green'],
  ['MEASURE', 'purple'],
  ['PICTOMETRY', 'green'],
  ['PLUME', 'red'],
  ['PRINT', 'purple'],
  ['EDITOR', 'green'],
  ['ZOOM_IN', 'blue'],
  ['ZOOM_OUT', 'blue'],
  ['ZOOM_TO_PREVIOUS_EXTENT', 'blue'],
  ['ZOOM_TO_NEXT_EXTENT', 'blue'],
  ['FULL_EXTENT', 'blue'],
  ['CLEAR_', 'red'],
  ['PAN', 'blue'],
  ['DMAT', 'red'],
  ['CLEAR', 'red'],
  ['PHOTO', 'green'],
  ['SUBSCRIPTION', 'red'],
  ['CURRENT_LOCATION', 'blue']
]);

const NO_CONTENT_SET = new Set([
  'ACTIVITYMANAGEMENT',
  'ZOOM_IN',
  'ZOOM_OUT',
  'FULL_EXTENT',
  'ZOOM_TO_PREVIOUS_EXTENT',
  'ZOOM_TO_NEXT_EXTENT',
  'CLEAR_',
  'PAN',
  'IDENTIFY',
  'PHOTO',
  'CLEAR',
  'BULKIMAGEUPLOADER',
  'SUBSCRIPTION',
  'CURRENT_LOCATION',
  'REPORTBUILDER'
]);

const ATOMIC_OP_SET = new Set([
  'ZOOM_IN',
  'ZOOM_OUT',
  'ZOOM_TO_PREVIOUS_EXTENT',
  'ZOOM_TO_NEXT_EXTENT',
  'FULL_EXTENT',
  'FEATUREDB',
  'CURRENT_LOCATION'
]);

class ToolsManager {
  /**
   * @param {object} config
   * @param {string} config.id
   */
  constructor (config) {
    this.id = config.id;

    this._tabTools = Mediator.serverData.TabTools ?? {};

    this.selectBtn = null;
    this.nextActiveToolName = null;
    this.isSelfClose = null;

    /** @type {Record<string, HTMLButtonElement>} */
    this.menuBtns = {};

    /** @type {Partial<FactoryTypes.ModuleMap>} */
    this._tools = {};
    /** @type {FactoryTypes.ModuleNames} */
    this.activeToolName = null;

    this._nodeHashByTab = _initialAllToolMenuButton(this);
  }

  loadMapNavigation () {
    this.getToolWidgetAsync('MapNavigation').then(mapNavigation => {
      this._tools.MapNavigation = mapNavigation;
    });
  }

  /**
   * @param {string} tabName
   */
  getToolsMenuButtonsForTab (tabName) {
    return _getToolsMenuButtonsForTab(this, tabName);
  }

  /**
   * @param {FactoryTypes.ModuleNames} toolName
   */
  // eslint-disable-next-line class-methods-use-this
  isMapNavTool (toolName) {
    return Mediator.MapNavExtraTools.includes(toolName);
  }

  tabHasStartupTools () {
    const startupTools = Mediator.serverData.startupTools;
    const toolNames = Object.keys(startupTools);

    return toolNames.some(
      name => name.toLowerCase() === 'search' || this.hasTool(name)
    );
  }

  bootStartupTools () {
    const startupTools = Mediator.serverData.startupTools;
    const toolNames = Object.keys(startupTools);

    for (let i = 0; i < toolNames.length; i++) {
      const name = toolNames[i];
      const input = startupTools[name];

      if (name === 'search') {
        /** @type {import('gisw/Widgets/GenericSearch/Search').default} */
        const searcher = Mediator.rightMenu.searchBar;
        searcher.txtSearch.value = input;
        searcher.executeSearch(input);
      } else if (this.hasTool(name)) {
        this.getToolWidgetAsync(name).then(widget => {
          this.activateTool(name, input, widget);
        });
      }
    }
  }

  /**
   * @param {FactoryTypes.ModuleNames} name
   * @param {*} input
   * @param {*} widget
   */
  activateTool (name, input, widget) {
    _toggleSelectBtnCSS(this, name, { target: $('#' + name + 'Btn')[0] });

    Mediator.handle({
      name: 'toolsManagerMenu.button.click',
      args: [widget, name, hasContent(name)]
    });

    this.bootUpSpecialTool(name, input);
  }

  /**
   * @param {FactoryTypes.ModuleNames} toolName
   */
  bootUpTool (toolName) {
    if (this.isMapNavTool(toolName)) {
      _bootUpMapNavTool(this, toolName);
    } else {
      this._callToolMethod(toolName, 'bootUp');
    }
  }

  /**
   * @param {FactoryTypes.ModuleNames} toolName
   * @param {*} arg
   */
  bootUpSpecialTool (toolName, arg) {
    this.activeToolName = toolName;
    this._callToolMethodSpecial(toolName, 'bootUp', [arg, false]);
  }

  bootUpActiveTool () {
    this.bootUpTool(this.activeToolName);
  }

  /**
   * @param {*} arg
   */
  bootUpSpecialActiveTool (arg) {
    this.bootUpSpecialTool(this.activeToolName, arg);
  }

  /**
   * @param {FactoryTypes.ModuleNames} toolName
   */
  shutdownTool (toolName) {
    if (this.isMapNavTool(toolName)) {
      if (this._tools.MapNavigation?.navToolbar) {
        this._tools.MapNavigation.navToolbar.deactivate();
      }
    } else {
      this._callToolMethod(toolName, 'shutdown');
    }
  }

  shutdownActiveTool () {
    if (Mediator.AlwaysOnIdentify) {
      Mediator.AlwaysOnIdentify.resume();
    }

    const activeTool = Mediator.leftPanel.getGWindow('activeTool');

    if (activeTool) {
      activeTool.destroy();
    }

    this.shutdownTool(this.activeToolName);
  }

  closeActiveTool (isSelfClose = false) {
    this.isSelfClose = isSelfClose;
    this.shutdownActiveTool();
    this.activeToolName = null;
    this.activeTool = null;
    this.isSelfClose = false;
  }

  /**
   * @template {FactoryTypes.ModuleNames} T
   * @param {T} toolName
   */
  getToolWidgetAsync (toolName) {
    if (this._tools[toolName]) {
      return Promise.resolve(this._tools[toolName]);
    } else {
      return Mediator.Factory.Create({
        name: toolName,
        params: {
          map: Mediator.map,
          _widgetName: toolName
        }
      }).then(widget => {
        if (widget.startup) {
          widget.startup();
        }

        widget.id = toolName;
        this._tools[toolName] = widget;

        return widget;
      });
    }
  }

  getToolColor (toolName) {
    return getToolColor(toolName);
  }

  /**
   * @template {FactoryTypes.ModuleNames} T
   * @param {*} domNode
   * @param {T} toolName
   * @param {*} [e]
   */
  buttonHandler (domNode, toolName, e) {
    if (this.activeToolName === toolName) {
      return;
    }

    if (this.activeToolName !== null) {
      // Remove tool from URL when using another tool
      toolUrl.clear();
    }

    _toggleSelectBtnCSS(this, toolName, e);

    this.getToolWidgetAsync(toolName)
      .then(widget => {
        this.nextActiveToolName = toolName;
        this._tools[toolName] = widget;

        if (isAtomicOpTool(toolName)) {
          this.bootUpTool(toolName);
          return;
        }

        this.shutdownActiveTool();

        Mediator.handle({
          name: 'toolsManagerMenu.button.click',
          args: [widget, toolName, hasContent(toolName)]
        });

        this.activeToolName = toolName;
        this.activeTool = widget;
        this.bootUpActiveTool();

        // When next active tool becomes the current active tool
        this.nextActiveToolName = null;
      })
      .catch(err => {
        Mediator.jstManager.jstWarn(
          'Tool',
          'ToolsManager',
          'buttonHandler',
          `Could not load "${toolName}" bundle. Checking for update.` +
            `\n${err.message}\n${err.stack}`
        );
        updateCheck();
      });
  }

  /**
   * This method is for when we want to bootUp with an argument
   * @param {FactoryTypes.ModuleNames} toolName
   * @param {any} arg
   */
  bootUpSpecial (toolName, arg) {
    _toggleSelectBtnCSS(this, toolName);

    return this.getToolWidgetAsync(toolName).then(widget => {
      this._tools[toolName] = widget;

      if (isAtomicOpTool(toolName)) {
        this.bootUpTool(toolName);
        return;
      }

      this.shutdownActiveTool();

      Mediator.handle({
        name: 'toolsManagerMenu.button.click',
        args: [widget, toolName, hasContent(toolName)]
      });

      this.activeToolName = toolName;
      this.activeTool = widget;
      this.bootUpSpecialActiveTool(arg);
    });
  }

  destroy () {
    for (const item in this._tools) {
      if (this._tools.hasOwnProperty(item)) {
        Mediator.destroyWidget(this._tools[item]);
      }
    }
  }

  /**
   * @param {FactoryTypes.ModuleNames} toolName
   * @param {string} method
   */
  _callToolMethod (toolName, method) {
    const tool = this._tools[toolName || '__NOOP__'];

    if (tool?.[method]) {
      tool[method]();
    }
  }

  /**
   * @param {FactoryTypes.ModuleNames} toolName
   * @param {string} method
   * @param {any} arg
   */
  _callToolMethodSpecial (toolName, method, arg) {
    const tool = this._tools[toolName || '__NOOP__'];

    if (tool?.[method]) {
      tool[method](arg);
    }
  }

  /**
   * @param {string} toolName
   * @param {string} [tabNameInput]
   */
  hasTool (toolName, tabNameInput) {
    const tabName = tabNameInput ?? Mediator.getCurrentTabName();

    if (!this._tabTools[tabName]) {
      return false;
    }

    for (let i = 0; i < this._tabTools[tabName].length; i++) {
      if (this._tabTools[tabName][i].Name === toolName) {
        return true;
      }
    }

    return false;
  }

  hasAdminTools () {
    const tabNames = Object.keys(this._tabTools);

    for (let j = 0; j < tabNames.length; j++) {
      const currentTabTools = this._tabTools[tabNames[j]];

      for (let i = 0; i < currentTabTools.length; i++) {
        if (currentTabTools[i].Name.indexOf('Admin_') >= 0) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * @param {FactoryTypes.ModuleNames} toolName
   */
  handleAlwaysOnIdentify (toolName) {
    if (PAUSE_ALWAYS_ON_ID.has(toolName)) {
      if (Mediator.AlwaysOnIdentify) {
        Mediator.AlwaysOnIdentify.pause();
      }
    }
  }

  bootUpPersonalProperty () {
    if (this._tools.PersonalProperty) {
      return Promise.resolve(this._tools.PersonalProperty);
    }

    return this.getToolWidgetAsync('PersonalProperty');
  }

  /**
   * @param {import('gisw/Widgets/GenericSearch/ResultSet').default} results
   * @param {string} searchString
   */
  searchPersonalProperty (results, searchString) {
    return new Promise(resolve => {
      this.bootUpPersonalProperty().then(widget => {
        widget
          .mixPersonalPropertyData(results, searchString)
          .then(mixedResults => {
            resolve(mixedResults);
          });
      });
    });
  }
}

ToolsManager.count = 0;

/**
 * @param {ToolsManager} toolsManager
 * @param {string} toolName
 */
function _bootUpMapNavTool (toolsManager, toolName) {
  const mnt = toolsManager._tools.MapNavigation;
  const map = Mediator.map;

  switch (toolName) {
    case 'Zoom_In':
      mnt.zoomIn();
      break;
    case 'Zoom_Out':
      mnt.zoomOut();
      break;
    case 'Pan':
      messageObserver.broadcast({
        title: 'Tool Hint - Pan',
        message: 'Click and drag the map to move',
        toolName: 'Pan'
      });
      break;
    case 'Full_Extent':
      Mediator.layermanager.zoomToCurrentLayerFullExtent(true);
      break;
    case 'Current_Location':
      mnt.toggleGeolocationTracking();
      break;
    case 'Zoom_To_Previous_Extent':
      if (map.get('previousExtents').length === 0) {
        messageObserver.broadcast({
          title: 'Message',
          message: 'No previous extent'
        });
      } else {
        _zoomAndTrack(map, map.get('previousExtents'), map.get('nextExtents'));
      }

      break;
    case 'Zoom_To_Next_Extent':
      if (map.get('nextExtents').length === 0) {
        messageObserver.broadcast({
          title: 'Message',
          message: 'No next extent'
        });
      } else {
        _zoomAndTrack(map, map.get('nextExtents'), map.get('previousExtents'));
      }

      break;
    case 'Clear_':
      Mediator.ClearGraphics(true, true);
      mnt.stopGeolocationTracking();

      Mediator.handle({ name: 'rightmenu.clearAll' });
      Mediator.handle({ name: 'remove.activeTool' });

      toolsManager.activeToolName = null;
      break;
  }
}

/**
 * @param {import('ol/Map').default} map
 * @param {Array} getArray
 * @param {Array} addArray
 */
function _zoomAndTrack (map, getArray, addArray) {
  const extent = getArray.pop();

  map.set('skipMoveEnd', true);

  addArray.push(map.get('currentExtent'));

  map.set('currentExtent', extent);

  map.getView().fit(extent, { duration: 200, nearest: true });
}

/**
 * @param {ToolsManager} that
 */
function _initialAllToolMenuButton (that) {
  /** @type {Record<string, ReturnType<getNodeHash>>} */
  const nodeHashes = {};
  const tabTools = that._tabTools;

  forIn(tabTools, function (tools, tab) {
    nodeHashes[tab] = getNodeHash(that, _getInitItemsForTab(tools));

    if (nodeHashes[tab].domNode) {
      nodeHashes[tab].domNode.id = tab + 'ToolsMenuPanel';
    }

    nodeHashes[tab].itemEventListener('click', that.buttonHandler.bind(that));
  });

  return nodeHashes;
}

/**
 * @param {Array<any>} tools
 */
function _getInitItemsForTab (tools) {
  const initItems = [];

  /** @type {Record<ToolColor, Array<any>>} */
  const colorGroups = type.groupBy(tools, tool => {
    const option = Mediator.serverData.FrontEndOption.UserSpecificTool;

    if (option && tool.Name === option.Value) {
      const certifiedUsers = JSON.parse(option.SubOptions);
      tool.ShowButton = !!certifiedUsers[Mediator.currentUser];
    }

    if (tool.ShowButton === true) {
      return getToolColor(tool.Name);
    } else {
      return null;
    }
  });

  for (const color of COLOR_KEYS) {
    if (!colorGroups.hasOwnProperty(color)) {
      continue;
    }

    const nextItems = colorGroups[color]
      .sort(_alphabeticalish)
      .filter(tool => !isHiddenTool(tool.Name))
      .map(tool => ({ key: tool.Name }));

    initItems.push(...nextItems.sort(_alphabeticalish));
  }

  return initItems;
}

/**
 * @param {ToolsManager} tw
 * @param {string} toolName
 * @param {*} [e]
 */
function _toggleSelectBtnCSS (tw, toolName, e) {
  if (!e?.target || isAtomicOpTool(toolName)) {
    return;
  }

  if (tw.selectBtn) {
    domClass.remove(tw.selectBtn, 'selected');
  }

  let target = e.target;

  while (target && target.tagName !== 'BUTTON') {
    target = target.parentNode;
  }

  if (target) {
    domClass.add(target, 'selected');
    tw.selectBtn = target;
  }
}

/**
 * @param {ToolsManager} tw
 * @param {string} tabName
 */
function _getToolsMenuButtonsForTab (tw, tabName) {
  const curTabTools = tw._nodeHashByTab[tabName];

  if (curTabTools) {
    return curTabTools;
  } else {
    Mediator.jstManager.jstError(
      'Tool',
      'ToolsManager',
      '_getToolsMenuButtonsForTab',
      'No tools avaible for ' + tabName + ' tab.'
    );
    messageObserver.broadcast({
      title: 'Warning',
      message: 'No tools avaible for ' + tabName + ' tab.'
    });
    return null;
  }
}

/**
 * @param {ToolsManager} tw
 * @param {Array<{ key: string; }>} initItems
 */
function getNodeHash (tw, initItems) {
  const nodeHash = new NodeHash({
    initItems,
    className: 'ThumbnailWindow',
    createNode (param) {
      const title = Mediator.ToolsTitleDiscription[param.key] || param.key;

      const container = document.createElement('div');
      container.classList.add('ThumbnailGrid');

      const button = document.createElement('button');
      button.id = param.key + 'Btn';
      button.title = title;
      button.className = 'GridMenu ' + param.key + 'Btn';
      button.setAttribute('data-tool-name', param.key);

      // Temporary way of making glyphicon for small tool
      // will need to scale better
      const icon = document.createElement('span');
      icon.setAttribute('aria-hidden', 'true');

      switch (param.key) {
        case 'TMRM':
          icon.classList.add('glyphicon', 'glyphicon-phone');
          break;
        case 'BRatio':
          icon.classList.add('glyphicon', 'glyphicon-bold');
          break;
      }

      button.appendChild(icon);

      const label = document.createElement('label');
      label.innerHTML = title;
      label.title = title;

      container.append(button, label);

      if (!tw.menuBtns) {
        tw.menuBtns = {};
      }

      tw.menuBtns[param.key] = button;

      return container;
    }
  });

  return nodeHash;
}

/**
 * @param {string} toolName
 */
function isHiddenTool (toolName) {
  return DONT_SHOW_SET.has(toolName.toUpperCase());
}

/**
 * @param {string} toolName
 */
function hasContent (toolName) {
  return !NO_CONTENT_SET.has(toolName.toUpperCase());
}

/**
 * @param {string} toolName
 */
function isAtomicOpTool (toolName) {
  return ATOMIC_OP_SET.has(toolName.toUpperCase());
}

/**
 * @param {string} toolName
 */
function getToolColor (toolName) {
  return COLOR_MAP.get(toolName.toUpperCase()) || 'green';
}

/**
 * 'Clear' always appears last in red color group.
 */
const clearRegExp = new RegExp('clear_?', 'i');
const locationRegExp = new RegExp('Current_Location', 'i');

function _alphabeticalish (t1, t2) {
  const key = t1.key ? 'key' : 'Name';

  const a = t1[key].toLowerCase();
  const b = t2[key].toLowerCase();

  if (locationRegExp.test(a)) {
    return 1;
  }

  if (locationRegExp.test(b)) {
    return -1;
  }

  if (
    clearRegExp.test(b) ||
    b === 'zoom_to_next_extent' && a === 'zoom_to_previous_extent' ||
    a < b
  ) {
    return -1;
  } else if (
    clearRegExp.test(a) ||
    a === 'zoom_to_next_extent' && b === 'zoom_to_previous_extent' ||
    a > b
  ) {
    return 1;
  } else {
    return 0;
  }
}

export default ToolsManager;
